import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  Menu,
  MenuItem,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { red, orange } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";

/** Simple scroll hook to flip styles after threshold */
function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

/** Navigation model (add children wherever you want a dropdown/collapsible) */
const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About Us",
    to: "/about",
    children: [
      { label: "Mission", to: "/mission" },
      { label: "Vision", to: "/vision" },
      { label: "Principal’s Message", to: "/principalmessage" },
    ],
  },
  {
    label: "Admission",
    to: "/admission",
    children: [
      { label: "Admission", to: "/admission" },
      { label: "Fee Structure", to: "/feestructure" },
      // { label: "Scholarship", to: "/admission" },
    ],
  },
  { label: "Course", to: "/courses" },
  { label: "Circular", to: "/circular" },
  { label: "Contact Us", to: "/contact" },
];

// Brand palette
// const BRAND = {
//   base: red[800],
//   scrolled: red[800],
//   on: "#FFFFFF", // text/icons on red
// };
const BRAND = {
  base: "#1976d2",      // calm blue
  scrolled: "#1565c0",  // darker shade for scrolled header
  on: "#FFFFFF",        // text/icons on blue
};

// Orange accent for hover text
// const ORANGE_ACCENT = "#ff9100"; // #ff9100

const ORANGE_ACCENT = "#ffb300";
// const HOVER = {
//   onBarBg: "#EAD196", // overlay on red app bar
//   onIconBg: "#EAD196",
//   onMenuBg: "#EAD196", // hover on white menus/drawer
//   onMenuText: "#F3EDC8",            // text accent on hover
//   // onBarBg: alpha(ORANGE_ACCENT, 0.22), // overlay on red app bar
//   // onIconBg: alpha(ORANGE_ACCENT, 0.22),
//   // onMenuBg: alpha(ORANGE_ACCENT, 0.12), // hover on white menus/drawer
//   // onMenuText: ORANGE_ACCENT,            // text accent on hover
// };

const HOVER = {
  onBarBg: "rgba(255, 179, 0, 0.18)", // subtle amber overlay on bar
  onIconBg: "rgba(255, 179, 0, 0.18)", 
  onMenuBg: "rgba(25, 118, 210, 0.08)", // blue tint on menus
  onMenuText: ORANGE_ACCENT,           // amber text accent
};

export default function Header() {
  const scrolled = useScrolled(8);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const toggleDrawer = (next) => () => setDrawerOpen(next);
  const { pathname } = useLocation();

  /** Desktop dropdown state */
  const [anchorEl, setAnchorEl] = React.useState(null); // anchor for the open dropdown
  const [openKey, setOpenKey] = React.useState(null);   // which parent is open

  const openDesktopMenu = (key) => (e) => {
    setAnchorEl(e.currentTarget);
    setOpenKey(key);
  };
  const closeDesktopMenu = () => {
    setAnchorEl(null);
    setOpenKey(null);
  };

  /** Mobile collapsible state */
  const [mobileOpenKey, setMobileOpenKey] = React.useState(null);
  const toggleMobileSection = (key) =>
    setMobileOpenKey((prev) => (prev === key ? null : key));

  const linkBaseStyles = {
    px: 1.5,
    py: 1,
    borderRadius: 1.5,
    transition: "all .2s ease",
    color: BRAND.on,
    "&:hover": {
      backgroundColor: HOVER.onBarBg,
      color: ORANGE_ACCENT, // <-- text turns orange on hover (app bar)
      transform: "translateY(-1px)",
      boxShadow: `0 6px 18px ${alpha("#000", 0.18)}`,
    },
  };

  const activeText = { color: BRAND.on, fontWeight: 800 };
  const inactiveText = { color: BRAND.on, fontWeight: 600 };

  const isPathActive = (to) => pathname === to;
  const isParentActive = (item) =>
    isPathActive(item.to) ||
    (item.children?.some((c) => pathname === c.to.split("#")[0]) ?? false);

  /** Desktop nav with dropdowns */
  const desktopNav = (
    <Box
      component="nav"
      sx={{
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {NAV.map((item) => {
        const parentActive = isParentActive(item);

        if (!item.children) {
          return (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              sx={{
                ...linkBaseStyles,
                ...(parentActive ? activeText : inactiveText),
                borderBottom: parentActive
                  ? `2px solid ${alpha(BRAND.on, 0.85)}`
                  : "2px solid transparent",
                // ensure nested text changes to orange on hover
                "&:hover .MuiListItemText-primary": { color: ORANGE_ACCENT },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: 15 }}
              />
            </ListItemButton>
          );
        }

        const open = openKey === item.label;

        return (
          <Box key={item.label} onMouseLeave={closeDesktopMenu}>
            <Button
              onMouseEnter={openDesktopMenu(item.label)}
              onFocus={openDesktopMenu(item.label)}
              onClick={openDesktopMenu(item.label)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    color: BRAND.on, // keep chevron white
                    transform: open ? "rotate(180deg)" : "none",
                    transition: "transform .2s",
                  }}
                />
              }
              sx={{
                ...linkBaseStyles,
                ...(parentActive ? activeText : inactiveText),
                fontSize: "15px",
                textTransform: "none",
                borderBottom: parentActive
                  ? `2px solid ${alpha(BRAND.on, 0.85)}`
                  : "2px solid transparent",
              }}
            >
              {item.label}
            </Button>

            <Menu
              anchorEl={open ? anchorEl : null}
              open={open}
              onClose={closeDesktopMenu}
              MenuListProps={{ onMouseLeave: closeDesktopMenu }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              keepMounted
              disableScrollLock
              PaperProps={{
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: 6,
                },
              }}
            >
              {item.children.map((child) => {
                const base = child.to.split("#")[0];
                const selected = pathname === base;
                return (
                  <MenuItem
                    key={child.to}
                    component={NavLink}
                    to={child.to}
                    onClick={closeDesktopMenu}
                    selected={selected}
                    sx={{
                      fontSize: "15px",
                      fontWeight: selected ? 700 : 600,
                      "&:hover": {
                        bgcolor: HOVER.onMenuBg,
                        color: HOVER.onMenuText, // <-- text turns orange
                      },
                    }}
                  >
                    {child.label}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        );
      })}

      {/* CTA (left as-is; your hover stays unchanged) */}
      {/* <Box sx={{ pl: 1 }}>
        <Button
          variant="contained"
          size="medium"
          sx={{
            borderRadius: 2,
            fontWeight: 800,
            bgcolor: BRAND.on,
            color: BRAND.scrolled,
            "&:hover": { bgcolor: alpha(BRAND.on, 0.9) }, // unchanged
          }}
          onClick={() => (window.location.href = "/admission")}
        >
          Apply Now
        </Button>
      </Box> */}
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 6 : 0}
        sx={{
          backgroundColor: scrolled
            ? alpha(BRAND.scrolled, 0.94)
            : alpha(BRAND.base, 0.82),
          backdropFilter: "saturate(180%) blur(10px)",
          transition: "background-color .25s ease, box-shadow .25s ease",
          borderBottom: scrolled
            ? `1px solid ${alpha("#000", 0.08)}`
            : "1px solid transparent",
          color: BRAND.on,
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Toolbar sx={{ minHeight: 72, px: 2 }}>
            {/* Logo / Brand */}
            <Box
              component={NavLink}
              to="/"
              aria-label="Go to homepage"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                gap: 1.25,
                mr: 2,
              }}
            >
              <Box
                component="img"
                src="/assets/images/logo/aisect_inter_college_logo.png"
                alt="AISECT Inter College Logo"
                sx={{ height: 44, width: "auto", display: "block" }}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Nav */}
            {desktopNav}

            {/* Mobile Menu Button */}
            <IconButton
              edge="end"
              aria-label="Open menu"
              onClick={toggleDrawer(true)}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                ml: 1,
                color: BRAND.on,
                "&:hover": {
                  bgcolor: HOVER.onIconBg,
                  color: ORANGE_ACCENT, // <-- icon turns orange on hover
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer to offset fixed AppBar */}
      <Toolbar sx={{ minHeight: 72 }} />

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: "84vw",
            maxWidth: 360,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            bgcolor: "background.paper",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            bgcolor: BRAND.base,
            color: BRAND.on,
            borderTopLeftRadius: 16,
          }}
        >
          <Box sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: 0.3 }}>
            Menu
          </Box>
          <IconButton
            aria-label="Close menu"
            onClick={toggleDrawer(false)}
            sx={{
              color: BRAND.on,
              "&:hover": { bgcolor: HOVER.onIconBg, color: ORANGE_ACCENT },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <List component="nav" sx={{ py: 1 }}>
          {NAV.map((item) => {
            const active = isParentActive(item);

            if (!item.children) {
              return (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  onClick={toggleDrawer(false)}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 1.5,
                    ...(active
                      ? {
                          bgcolor: "action.selected",
                          color: BRAND.base,
                          fontWeight: 700,
                        }
                      : {}),
                    "&:hover": {
                      bgcolor: HOVER.onMenuBg,
                      // ensure nested text turns orange
                      "& .MuiListItemText-primary": { color: ORANGE_ACCENT },
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: active ? 700 : 600,
                    }}
                  />
                </ListItemButton>
              );
            }

            const open = mobileOpenKey === item.label;

            return (
              <Box key={item.label} sx={{ mx: 1, my: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleMobileSection(item.label)}
                  sx={{
                    borderRadius: 1.5,
                    ...(active
                      ? {
                          bgcolor: "action.selected",
                          color: BRAND.base,
                          fontWeight: 700,
                        }
                      : {}),
                    "&:hover": {
                      bgcolor: HOVER.onMenuBg,
                      "& .MuiListItemText-primary": { color: ORANGE_ACCENT },
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: active ? 700 : 600,
                    }}
                  />
                  {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const base = child.to.split("#")[0];
                      const childActive = pathname === base;
                      return (
                        <ListItemButton
                          key={child.to}
                          component={NavLink}
                          to={child.to}
                          onClick={toggleDrawer(false)}
                          sx={{
                            ml: 2,
                            my: 0.25,
                            borderRadius: 1.25,
                            ...(childActive
                              ? {
                                  bgcolor: "action.selected",
                                  color: BRAND.base,
                                  fontWeight: 700,
                                }
                              : {}),
                            "&:hover": {
                              bgcolor: HOVER.onMenuBg,
                              "& .MuiListItemText-primary": {
                                color: ORANGE_ACCENT,
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: 15,
                              fontWeight: childActive ? 700 : 600,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>

        {/* Drawer CTA (left as-is; your hover remains red) */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              borderRadius: 2,
              fontWeight: 800,
              bgcolor: BRAND.base,
              color: BRAND.on,
              "&:hover": { bgcolor: BRAND.scrolled }, // unchanged
            }}
            onClick={() => {
              setDrawerOpen(false);
              window.location.href = "/admission/apply";
            }}
          >
            Apply Now
          </Button>
        </Box>
      </Drawer>
    </>
  );
}


// import React from "react";
// import { NavLink, useLocation } from "react-router-dom";
// import {
//   AppBar,
//   Toolbar,
//   Container,
//   Box,
//   IconButton,
//   Drawer,
//   List,
//   ListItemButton,
//   ListItemText,
//   Button,
//   Divider,
//   Menu,
//   MenuItem,
//   Collapse,
// } from "@mui/material";
// import {
//   Menu as MenuIcon,
//   Close as CloseIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
// } from "@mui/icons-material";
// import { red } from "@mui/material/colors";

// /** Simple scroll hook to flip styles after threshold */
// function useScrolled(threshold = 8) {
//   const [scrolled, setScrolled] = React.useState(false);
//   React.useEffect(() => {
//     const onScroll = () => setScrolled(window.scrollY > threshold);
//     onScroll();
//     window.addEventListener("scroll", onScroll, { passive: true });
//     return () => window.removeEventListener("scroll", onScroll);
//   }, [threshold]);
//   return scrolled;
// }

// /** Navigation model (add children wherever you want a dropdown/collapsible) */
// const NAV = [
//   { label: "Home", to: "/" },
//   {
//     label: "About Us",
//     to: "/about",
//     children: [
//       { label: "Mission", to: "/about#mission" },
//       { label: "Vision", to: "/about#vision" },
//       { label: "Principal’s Message", to: "/about#principalmessage" },
//     ],
//   },
//   {
//     label: "Admission",
//     to: "/admission",
//     children: [
//       { label: "Apply Online", to: "/admission" },
//       { label: "Fee Structure", to: "/admission" },
//       { label: "Scholarship", to: "/admission" },
//     ],
//   },
//   { label: "Event", to: "/event" },
//   { label: "Circular", to: "/circular" },
//   { label: "Contact Us", to: "/contact" },
// ];

// export default function Header() {
//   const scrolled = useScrolled(8);
//   const [drawerOpen, setDrawerOpen] = React.useState(false);
//   const toggleDrawer = (next) => () => setDrawerOpen(next);
//   const { pathname } = useLocation();

//   /** Desktop dropdown state */
//   const [anchorEl, setAnchorEl] = React.useState(null); // anchor for the open dropdown
//   const [openKey, setOpenKey] = React.useState(null);   // which parent is open

//   const openDesktopMenu = (key) => (e) => {
//     setAnchorEl(e.currentTarget);
//     setOpenKey(key);
//   };
//   const closeDesktopMenu = () => {
//     setAnchorEl(null);
//     setOpenKey(null);
//   };

//   /** Mobile collapsible state */
//   const [mobileOpenKey, setMobileOpenKey] = React.useState(null);
//   const toggleMobileSection = (key) =>
//     setMobileOpenKey((prev) => (prev === key ? null : key));

//   const linkBaseStyles = {
//     px: 1.5,
//     py: 1,
//     borderRadius: 1.5,
//     transition: "all .2s ease",
//     "&:hover": {
//       backgroundColor: "action.hover",
//       transform: "translateY(-1px)",
//       boxShadow: (t) => `0 6px 18px ${t.palette.action.hover}`,
//     },
//   };

//   const isPathActive = (to) => pathname === to;
//   const isParentActive = (item) =>
//     isPathActive(item.to) ||
//     (item.children?.some((c) => pathname === c.to.split("#")[0]) ?? false);

//   /** Desktop nav with dropdowns */
//   const desktopNav = (
//     <Box
//       component="nav"
//       sx={{
//         display: { xs: "none", md: "flex" },
//         alignItems: "center",
//         gap: 0.5,
//       }}
//     >
//       {NAV.map((item) => {
//         const parentActive = isParentActive(item);

//         if (!item.children) {
//           return (
//             <ListItemButton
//               key={item.to}
//               component={NavLink}
//               to={item.to}
//               sx={{
//                 ...linkBaseStyles,
//                 color: parentActive ? "primary.main" : "text.primary",
//                 fontWeight: parentActive ? 700 : 600,
//                 "&.active": { color: "primary.main", fontWeight: 700 },
//               }}
//             >
//               <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 15 }} />
//             </ListItemButton>
//           );
//         }

//         const open = openKey === item.label;

//         return (
//           <Box key={item.label} onMouseLeave={closeDesktopMenu}>
//             <Button
//               onMouseEnter={openDesktopMenu(item.label)}
//               onFocus={openDesktopMenu(item.label)}
//               onClick={openDesktopMenu(item.label)}
//               endIcon={
//                 <ExpandMoreIcon
//                   sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}
//                 />
//               }
//               sx={{
//                 ...linkBaseStyles,
//                 color: parentActive ? "primary.main" : "text.primary",
//                 fontWeight: parentActive ? 700 : 600,
//                 fontSize: "15px", // match non-dropdown size
//                 textTransform: "none",
//               }}
//             >
//               {item.label}
//             </Button>

//             <Menu
//               anchorEl={open ? anchorEl : null}
//               open={open}
//               onClose={closeDesktopMenu}
//               MenuListProps={{ onMouseLeave: closeDesktopMenu }}
//               anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
//               transformOrigin={{ vertical: "top", horizontal: "left" }}
//               keepMounted
//               disableScrollLock
//             >
//               {item.children.map((child) => (
//                 <MenuItem
//                   key={child.to}
//                   component={NavLink}
//                   to={child.to}
//                   onClick={closeDesktopMenu}
//                   selected={pathname === child.to.split("#")[0]}
//                   sx={{
//                     fontSize: "15px",
//                     fontWeight: pathname === child.to.split("#")[0] ? 700 : 600,
//                   }}
//                 >
//                   {child.label}
//                 </MenuItem>
//               ))}
//             </Menu>
//           </Box>
//         );
//       })}

//       {/* CTA */}
//       <Box sx={{ pl: 1 }}>
//         <Button
//           variant="contained"
//           size="medium"
//           sx={{ borderRadius: 2, fontWeight: 700 }}
//           onClick={() => (window.location.href = "/admission/apply")}
//         >
//           Apply Now
//         </Button>
//       </Box>
//     </Box>
//   );

//   return (
//     <>
//       <AppBar
//         position="fixed"
//         elevation={scrolled ? 6 : 0}
//         sx={{
//           // backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.6)",
//           backgroundColor: scrolled
//       ? `${red[500]}E6` // darker red when scrolled
//       : `${red[500]}99`,
      
//           // backdropFilter: "saturate(180%) blur(10px)",
//           backdropFilter: "saturate(180%) blur(10px)",
//     color: "#fff",
//           color: "text.primary",
//           transition: "background-color .25s ease, box-shadow .25s ease",
//           borderBottom: (t) =>
//       scrolled ? `1px solid ${t.palette.divider}` : "1px solid transparent",
//   }}
//           // borderBottom: (t) =>
//           //   scrolled ? `1px solid ${t.palette.divider}` : "1px solid transparent",
//         // }}
//       >
//         <Container maxWidth="lg" disableGutters>
//           <Toolbar sx={{ minHeight: 72, px: 2 }}>
//             {/* Logo / Brand */}
//             <Box
//               component={NavLink}
//               to="/"
//               aria-label="Go to homepage"
//               sx={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 textDecoration: "none",
//                 gap: 1.25,
//                 mr: 2,
//               }}
//             >
//               <Box
//                 component="img"
//                 src="/assets/images/logo/aisect_inter_college_logo.png"
//                 alt="AISECT Inter College Logo"
//                 sx={{ height: 44, width: "auto", display: "block" }}
//               />
//             </Box>

//             <Box sx={{ flexGrow: 1 }} />

//             {/* Desktop Nav */}
//             {desktopNav}

//             {/* Mobile Menu Button */}
//             <IconButton
//               edge="end"
//               aria-label="Open menu"
//               onClick={toggleDrawer(true)}
//               sx={{ display: { xs: "inline-flex", md: "none" }, ml: 1 }}
//             >
//               <MenuIcon />
//             </IconButton>
//           </Toolbar>
//         </Container>
//       </AppBar>

//       {/* Spacer to offset fixed AppBar */}
//       <Toolbar sx={{ minHeight: 72 }} />

//       {/* Mobile Drawer */}
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={toggleDrawer(false)}
//         PaperProps={{
//           sx: {
//             width: "84vw",
//             maxWidth: 360,
//             borderTopLeftRadius: 16,
//             borderBottomLeftRadius: 16,
//             backgroundColor: "background.paper",
//           },
//         }}
//       >
//         <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
//           <Box sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: 0.3 }}>
//             Menu
//           </Box>
//           <IconButton aria-label="Close menu" onClick={toggleDrawer(false)}>
//             <CloseIcon />
//           </IconButton>
//         </Box>
//         <Divider />

//         <List component="nav" sx={{ py: 1 }}>
//           {NAV.map((item) => {
//             const active = isParentActive(item);

//             if (!item.children) {
//               return (
//                 <ListItemButton
//                   key={item.to}
//                   component={NavLink}
//                   to={item.to}
//                   onClick={toggleDrawer(false)}
//                   sx={{
//                     mx: 1,
//                     my: 0.5,
//                     borderRadius: 1.5,
//                     ...(active
//                       ? { bgcolor: "action.selected", color: "primary.main", fontWeight: 700 }
//                       : {}),
//                   }}
//                 >
//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: active ? 700 : 600,
//                     }}
//                   />
//                 </ListItemButton>
//               );
//             }

//             const open = mobileOpenKey === item.label;

//             return (
//               <Box key={item.label} sx={{ mx: 1, my: 0.5 }}>
//                 <ListItemButton
//                   onClick={() => toggleMobileSection(item.label)}
//                   sx={{
//                     borderRadius: 1.5,
//                     ...(active
//                       ? { bgcolor: "action.selected", color: "primary.main", fontWeight: 700 }
//                       : {}),
//                   }}
//                 >
//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: active ? 700 : 600,
//                     }}
//                   />
//                   {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                 </ListItemButton>

//                 <Collapse in={open} timeout="auto" unmountOnExit>
//                   <List component="div" disablePadding>
//                     {item.children.map((child) => {
//                       const base = child.to.split("#")[0];
//                       const childActive = pathname === base;
//                       return (
//                         <ListItemButton
//                           key={child.to}
//                           component={NavLink}
//                           to={child.to}
//                           onClick={toggleDrawer(false)}
//                           sx={{
//                             ml: 2,
//                             my: 0.25,
//                             borderRadius: 1.25,
//                             ...(childActive
//                               ? { bgcolor: "action.selected", color: "primary.main", fontWeight: 700 }
//                               : {}),
//                           }}
//                         >
//                           <ListItemText
//                             primary={child.label}
//                             primaryTypographyProps={{
//                               fontSize: 15,
//                               fontWeight: childActive ? 700 : 600,
//                             }}
//                           />
//                         </ListItemButton>
//                       );
//                     })}
//                   </List>
//                 </Collapse>
//               </Box>
//             );
//           })}
//         </List>

//         <Box sx={{ px: 2, pb: 2 }}>
//           <Button
//             fullWidth
//             variant="contained"
//             size="large"
//             sx={{ borderRadius: 2, fontWeight: 700 }}
//             onClick={() => {
//               setDrawerOpen(false);
//               window.location.href = "/admission";
//             }}
//           >
//             Apply Now
//           </Button>
//         </Box>
//       </Drawer>
//     </>
//   );
// }


// import React from "react";
// import { NavLink, useLocation } from "react-router-dom";
// import {
//   AppBar,
//   Toolbar,
//   Container,
//   Box,
//   IconButton,
//   Drawer,
//   List,
//   ListItemButton,
//   ListItemText,
//   Button,
//   Divider,
//   Menu,
//   MenuItem,
//   Collapse,
// } from "@mui/material";
// import {
//   Menu as MenuIcon,
//   Close as CloseIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
// } from "@mui/icons-material";

// /** Simple scroll hook to flip styles after threshold */
// function useScrolled(threshold = 8) {
//   const [scrolled, setScrolled] = React.useState(false);
//   React.useEffect(() => {
//     const onScroll = () => setScrolled(window.scrollY > threshold);
//     onScroll();
//     window.addEventListener("scroll", onScroll, { passive: true });
//     return () => window.removeEventListener("scroll", onScroll);
//   }, [threshold]);
//   return scrolled;
// }

// /** Navigation model (add children wherever you want a dropdown/collapsible) */
// const NAV = [
//   { label: "Home", to: "/" },
//   {
//     label: "About Us",
//     to: "/about",
//     children: [
//       { label: "Mission", to: "/about/mission" },
//       { label: "Vision", to: "/about/vision" },
//       { label: "Principal's Message", to: "/principalmessage" },
//     ],
//   },
//   {
//     label: "Admission",
//     to: "/admission",
//     children: [
//       { label: "Apply Online", to: "/admission/apply" },
//       { label: "Fee Structure", to: "/admission/fees" },
//       { label: "Scholarship", to: "/admission/scholarship" },
//     ],
//   },
//   { label: "Event", to: "/event" },
//   { label: "Circular", to: "/circular" },
//   { label: "Contact Us", to: "/contact" },
// ];

// export default function Header() {
//   const scrolled = useScrolled(8);
//   const [drawerOpen, setDrawerOpen] = React.useState(false);
//   const toggleDrawer = (next) => () => setDrawerOpen(next);
//   const { pathname } = useLocation();

//   /** Desktop dropdown state */
//   const [anchorEl, setAnchorEl] = React.useState(null); // anchor for the open dropdown
//   const [openKey, setOpenKey] = React.useState(null);   // which parent is open

//   const openDesktopMenu = (key) => (e) => {
//     setAnchorEl(e.currentTarget);
//     setOpenKey(key);
//   };
//   const closeDesktopMenu = () => {
//     setAnchorEl(null);
//     setOpenKey(null);
//   };

//   /** Mobile collapsible state */
//   const [mobileOpenKey, setMobileOpenKey] = React.useState(null);
//   const toggleMobileSection = (key) =>
//     setMobileOpenKey((prev) => (prev === key ? null : key));

//   const linkBaseStyles = {
//     px: 1.5,
//     py: 1,
//     borderRadius: 1.5,
//     transition: "all .2s ease",
//     "&:hover": {
//       backgroundColor: "action.hover",
//       transform: "translateY(-1px)",
//       boxShadow: (t) => `0 6px 18px ${t.palette.action.hover}`,
//     },
//   };

//   const isPathActive = (to) => pathname === to;
//   const isParentActive = (item) =>
//     isPathActive(item.to) ||
//     (item.children?.some((c) => isPathActive(c.to)) ?? false);

//   /** Desktop nav with dropdowns */
//   const desktopNav = (
//     <Box
//       component="nav"
//       sx={{
//         display: { xs: "none", md: "flex" },
//         alignItems: "center",
//         gap: 0.5,
//       }}
//     >
//       {NAV.map((item) => {
//         const parentActive = isParentActive(item);

//         if (!item.children) {
//           return (
//             <ListItemButton
//               key={item.to}
//               component={NavLink}
//               to={item.to}
//               sx={{
//                 ...linkBaseStyles,
//                 color: parentActive ? "primary.main" : "text.primary",
//                 fontWeight: parentActive ? 700 : 600,
//                 "&.active": { color: "primary.main", fontWeight: 700 },
//               }}
//             >
//               <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 15 }} />
//             </ListItemButton>
//           );
//         }

//         const open = openKey === item.label;

//         return (
//           <Box key={item.label} onMouseLeave={closeDesktopMenu}>
//             {/* <Button
//               onMouseEnter={openDesktopMenu(item.label)}
//               onFocus={openDesktopMenu(item.label)}
//               onClick={openDesktopMenu(item.label)}
//               endIcon={
//                 <ExpandMoreIcon
//                   sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}
//                 />
//               }
//               sx={{
//                 ...linkBaseStyles,
//                 color: parentActive ? "primary.main" : "text.primary",
//                 fontWeight: parentActive ? 700 : 600,
//                 textTransform: "none",
//               }}
//             >
//               {item.label}
//             </Button> */}

//             <Button
//   onMouseEnter={openDesktopMenu(item.label)}
//   onFocus={openDesktopMenu(item.label)}
//   onClick={openDesktopMenu(item.label)}
//   endIcon={
//     <ExpandMoreIcon
//       sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}
//     />
//   }
//   sx={{
//     ...linkBaseStyles,
//     color: parentActive ? "primary.main" : "text.primary",
//     fontWeight: parentActive ? 700 : 600,
//     fontSize: "15px", // <-- Match non-dropdown font size
//     textTransform: "none",
//   }}
// >
//   {item.label}
// </Button>


//             <Menu
//               anchorEl={open ? anchorEl : null}
//               open={open}
//               onClose={closeDesktopMenu}
//               MenuListProps={{ onMouseLeave: closeDesktopMenu }}
//               anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
//               transformOrigin={{ vertical: "top", horizontal: "left" }}
//               keepMounted
//               disableScrollLock
//             >
//               {item.children.map((child) => (
//                 <MenuItem
//                   key={child.to}
//                   component={NavLink}
//                   to={child.to}
//                   onClick={closeDesktopMenu}
//                   selected={pathname === child.to}
//                    sx={{
//     fontSize: "15px",      // match nav text size
//     fontWeight: pathname === child.to ? 700 : 600,
//   }}
//                 >
//                   {child.label}
//                 </MenuItem>
//               ))}
//             </Menu>
//           </Box>
//         );
//       })}

//       {/* Optional CTA — remove if not needed */}
//       <Box sx={{ pl: 1 }}>
//         <Button
//           variant="contained"
//           size="medium"
//           sx={{ borderRadius: 2, fontWeight: 700 }}
//           onClick={() => (window.location.href = "/admission/apply")}
//         >
//           Apply Now
//         </Button>
//       </Box>
//     </Box>
//   );

//   return (
//     <>
//       <AppBar
//         position="fixed"
//         elevation={scrolled ? 6 : 0}
//         sx={{
//           backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.6)",
//           backdropFilter: "saturate(180%) blur(10px)",
//           color: "text.primary",
//           transition: "background-color .25s ease, box-shadow .25s ease",
//           borderBottom: (t) =>
//             scrolled ? `1px solid ${t.palette.divider}` : "1px solid transparent",
//         }}
//       >
//         <Container maxWidth="lg" disableGutters>
//           <Toolbar sx={{ minHeight: 72, px: 2 }}>
//             {/* Logo / Brand */}
//             <Box
//               component={NavLink}
//               to="/"
//               aria-label="Go to homepage"
//               sx={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 textDecoration: "none",
//                 gap: 1.25,
//                 mr: 2,
//               }}
//             >
//               <Box
//                 component="img"
//                 src="/assets/images/logo/aisect_inter_college_logo.png"
//                 alt="AISECT University Logo"
//                 sx={{ height: 44, width: "auto", display: "block" }}
//               />
//             </Box>

//             <Box sx={{ flexGrow: 1 }} />

//             {/* Desktop Nav */}
//             {desktopNav}

//             {/* Mobile Menu Button */}
//             <IconButton
//               edge="end"
//               aria-label="Open menu"
//               onClick={toggleDrawer(true)}
//               sx={{ display: { xs: "inline-flex", md: "none" }, ml: 1 }}
//             >
//               <MenuIcon />
//             </IconButton>
//           </Toolbar>
//         </Container>
//       </AppBar>

//       {/* Spacer to offset fixed AppBar */}
//       <Toolbar sx={{ minHeight: 72 }} />

//       {/* Mobile Drawer */}
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={toggleDrawer(false)}
//         PaperProps={{
//           sx: {
//             width: "84vw",
//             maxWidth: 360,
//             borderTopLeftRadius: 16,
//             borderBottomLeftRadius: 16,
//             backgroundColor: "background.paper",
//           },
//         }}
//       >
//         <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
//           <Box sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: 0.3 }}>
//             Menu
//           </Box>
//           <IconButton aria-label="Close menu" onClick={toggleDrawer(false)}>
//             <CloseIcon />
//           </IconButton>
//         </Box>
//         <Divider />

//         <List component="nav" sx={{ py: 1 }}>
//           {NAV.map((item) => {
//             const active = isParentActive(item);

//             if (!item.children) {
//               return (
//                 <ListItemButton
//                   key={item.to}
//                   component={NavLink}
//                   to={item.to}
//                   onClick={toggleDrawer(false)}
//                   sx={{
//                     mx: 1,
//                     my: 0.5,
//                     borderRadius: 1.5,
//                     ...(active
//                       ? { bgcolor: "action.selected", color: "primary.main", fontWeight: 700 }
//                       : {}),
//                   }}
//                 >
//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: active ? 700 : 600,
//                     }}
//                   />
//                 </ListItemButton>
//               );
//             }

//             const open = mobileOpenKey === item.label;

//             return (
//               <Box key={item.label} sx={{ mx: 1, my: 0.5 }}>
//                 <ListItemButton
//                   onClick={() => toggleMobileSection(item.label)}
//                   sx={{
//                     borderRadius: 1.5,
//                     ...(active
//                       ? { bgcolor: "action.selected", color: "primary.main", fontWeight: 700 }
//                       : {}),
//                   }}
//                 >
//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: active ? 700 : 600,
//                     }}
//                   />
//                   {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                 </ListItemButton>

//                 <Collapse in={open} timeout="auto" unmountOnExit>
//                   <List component="div" disablePadding>
//                     {item.children.map((child) => {
//                       const childActive = pathname === child.to;
//                       return (
//                         <ListItemButton
//                           key={child.to}
//                           component={NavLink}
//                           to={child.to}
//                           onClick={toggleDrawer(false)}
//                           sx={{
//                             ml: 2,
//                             my: 0.25,
//                             borderRadius: 1.25,
//                             ...(childActive
//                               ? { bgcolor: "action.selected", color: "primary.main", fontWeight: 700 }
//                               : {}),
//                           }}
//                         >
//                           <ListItemText
//                             primary={child.label}
//                             primaryTypographyProps={{
//                               fontSize: 15,
//                               fontWeight: childActive ? 700 : 600,
//                             }}
//                           />
//                         </ListItemButton>
//                       );
//                     })}
//                   </List>
//                 </Collapse>
//               </Box>
//             );
//           })}
//         </List>

//         <Box sx={{ px: 2, pb: 2 }}>
//           <Button
//             fullWidth
//             variant="contained"
//             size="large"
//             sx={{ borderRadius: 2, fontWeight: 700 }}
//             onClick={() => {
//               setDrawerOpen(false);
//               window.location.href = "/admission/apply";
//             }}
//           >
//             Apply Now
//           </Button>
//         </Box>
//       </Drawer>
//     </>
//   );
// }


// import React from "react";
// import { NavLink, useLocation } from "react-router-dom";
// import {
//   AppBar,
//   Toolbar,
//   Container,
//   Box,
//   IconButton,
//   Drawer,
//   List,
//   ListItemButton,
//   ListItemText,
//   Button,
//   Divider,
// } from "@mui/material";
// import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";

// /** Simple scroll hook to flip styles after threshold */
// function useScrolled(threshold = 8) {
//   const [scrolled, setScrolled] = React.useState(false);
//   React.useEffect(() => {
//     const onScroll = () => setScrolled(window.scrollY > threshold);
//     onScroll();
//     window.addEventListener("scroll", onScroll, { passive: true });
//     return () => window.removeEventListener("scroll", onScroll);
//   }, [threshold]);
//   return scrolled;
// }

// const NAV_LINKS = [
//   { label: "Home", to: "/" },
//   { label: "About Us", to: "/about" },
//   { label: "Admission", to: "/admission" },
//   { label: "Event", to: "/event" },
//   { label: "Circular", to: "/circular" },
//   { label: "Contact Us", to: "/contact" },
// ];

// export default function Header() {
//   const scrolled = useScrolled(8);
//   const [open, setOpen] = React.useState(false);
//   const toggle = (next) => () => setOpen(next);
//   const { pathname } = useLocation();

//   const linkBaseStyles = {
//     px: 1.5,
//     py: 1,
//     borderRadius: 1.5,
//     transition: "all .2s ease",
//     "&:hover": {
//       backgroundColor: "action.hover",
//       transform: "translateY(-1px)",
//       boxShadow: (t) => `0 6px 18px ${t.palette.action.hover}`,
//     },
//   };

//   const desktopNav = (
//     <Box
//       component="nav"
//       sx={{
//         display: { xs: "none", md: "flex" },
//         alignItems: "center",
//         gap: 0.5,
//       }}
//     >
//       {NAV_LINKS.map((item) => {
//         const isActive = pathname === item.to;
//         return (
//           <ListItemButton
//             key={item.to}
//             component={NavLink}
//             to={item.to}
//             sx={{
//               ...linkBaseStyles,
//               color: isActive ? "primary.main" : "text.primary",
//               fontWeight: isActive ? 700 : 600,
//               "&.active": { color: "primary.main", fontWeight: 700 },
//             }}
//           >
//             <ListItemText
//               primary={item.label}
//               primaryTypographyProps={{ fontSize: 15 }}
//             />
//           </ListItemButton>
//         );
//       })}
//       {/* Optional CTA — remove if not needed */}
//       <Box sx={{ pl: 1 }}>
//         <Button
//           variant="contained"
//           size="medium"
//           sx={{ borderRadius: 2, fontWeight: 700 }}
//           onClick={() => (window.location.href = "/admission")}
//         >
//           Apply Now
//         </Button>
//       </Box>
//     </Box>
//   );

//   return (
//     <>
//       <AppBar
//         position="fixed"
//         elevation={scrolled ? 6 : 0}
//         sx={{
//           backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.6)",
//           backdropFilter: "saturate(180%) blur(10px)",
//           color: "text.primary",
//           transition: "background-color .25s ease, box-shadow .25s ease",
//           borderBottom: (t) =>
//             scrolled ? `1px solid ${t.palette.divider}` : "1px solid transparent",
//         }}
//       >
//         <Container maxWidth="lg" disableGutters>
//           <Toolbar sx={{ minHeight: 72, px: 2 }}>
//             {/* Logo / Brand */}
//             <Box
//               component={NavLink}
//               to="/"
//               aria-label="Go to homepage"
//               sx={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 textDecoration: "none",
//                 gap: 1.25,
//                 mr: 2,
//               }}
//             >
//               <Box
//                 component="img"
//                 src="/assets/images/logo/aisect_inter_college_logo.png"
//                 alt="AISECT University Logo"
//                 sx={{ height: 44, width: "auto", display: "block" }}
//               />
//             </Box>

//             <Box sx={{ flexGrow: 1 }} />

//             {/* Desktop Nav */}
//             {desktopNav}

//             {/* Mobile Menu Button */}
//             <IconButton
//               edge="end"
//               aria-label="Open menu"
//               onClick={toggle(true)}
//               sx={{ display: { xs: "inline-flex", md: "none" }, ml: 1 }}
//             >
//               <MenuIcon />
//             </IconButton>
//           </Toolbar>
//         </Container>
//       </AppBar>

//       {/* Spacer to offset fixed AppBar */}
//       <Toolbar sx={{ minHeight: 72 }} />

//       {/* Mobile Drawer */}
//       <Drawer
//         anchor="right"
//         open={open}
//         onClose={toggle(false)}
//         PaperProps={{
//           sx: {
//             width: "84vw",
//             maxWidth: 360,
//             borderTopLeftRadius: 16,
//             borderBottomLeftRadius: 16,
//             backgroundColor: "background.paper",
//           },
//         }}
//       >
//         <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
//           <Box sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: 0.3 }}>
//             Menu
//           </Box>
//           <IconButton aria-label="Close menu" onClick={toggle(false)}>
//             <CloseIcon />
//           </IconButton>
//         </Box>
//         <Divider />
//         <List component="nav" sx={{ py: 1 }}>
//           {NAV_LINKS.map((item) => {
//             const isActive = pathname === item.to;
//             return (
//               <ListItemButton
//                 key={item.to}
//                 component={NavLink}
//                 to={item.to}
//                 onClick={toggle(false)}
//                 sx={{
//                   mx: 1,
//                   my: 0.5,
//                   borderRadius: 1.5,
//                   ...(isActive
//                     ? {
//                         bgcolor: "action.selected",
//                         color: "primary.main",
//                         fontWeight: 700,
//                       }
//                     : {}),
//                 }}
//               >
//                 <ListItemText
//                   primary={item.label}
//                   primaryTypographyProps={{
//                     fontSize: 16,
//                     fontWeight: isActive ? 700 : 600,
//                   }}
//                 />
//               </ListItemButton>
//             );
//           })}
//         </List>
//         <Box sx={{ px: 2, pb: 2 }}>
//           <Button
//             fullWidth
//             variant="contained"
//             size="large"
//             sx={{ borderRadius: 2, fontWeight: 700 }}
//             onClick={() => {
//               setOpen(false);
//               window.location.href = "/admission";
//             }}
//           >
//             Apply Now
//           </Button>
//         </Box>
//       </Drawer>
//     </>
//   );
// }


// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import {
//   AppBar,
//   Toolbar,
//   Typography,
//   List,
//   ListItem,
//   ListItemText,
//   Container,
//   IconButton,
//   Drawer,
//   Box,
// } from "@mui/material";
// import { Menu, Close } from "@mui/icons-material";

// export default function Header() {
//   const [isSticky, setIsSticky] = useState(false);
//   const [isDrawerOpen, setIsDrawerOpen] = useState(false);

//   const handleScroll = () => {
//     const isScrolled = window.scrollY > 0;
//     setIsSticky(isScrolled);
//   };

//   useEffect(() => {
//     window.addEventListener("scroll", handleScroll);
//     return () => {
//       window.removeEventListener("scroll", handleScroll);
//     };
//   }, []);

//   const toggleDrawer = () => {
//     setIsDrawerOpen(!isDrawerOpen);
//   };

//   return (
//     <>
//       <AppBar
//         position={isSticky ? "fixed" : "fixed"}
//         sx={{ backgroundColor: " #FDFDFEFF" }}
//       >
//         <Toolbar>
//           <Box
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               width: "100%",
//             }}
//           >
//             <Link
//               to="/"
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 textDecoration: "none",
//               }}
//             >
//               <img
//                 src="assets/images/logo/aisect_inter_college_logo.png"
//                 alt="Education Logo Images"
//                 style={{ height: "50px", width: "auto" }}
//               />
//             </Link>
//             <Container>
//               <List
//                 sx={{
//                   display: { xs: "none", md: "flex" },
//                 }}
//                 // className="ft-link"
//               >
//                 <ListItem
//                   component={Link}
//                   to="/"
//                   sx={{
//                     transition: "transform 0.3s, box-shadow 0.3s",
//                     "&:hover": {
//                       transform: "scale(1.05)",
//                       boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                     },
//                     // "&:hover": {
//                     //   textDecoration: "underline blue",
//                     //   color: "black",
//                     // },
//                   }}
//                 >
//                   <ListItemText
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                       fontWeight: "bold",
//                     }}
//                   >
//                     Home
//                   </ListItemText>
//                 </ListItem>
//                 <ListItem
//                   component={Link}
//                   to="/about"
//                   sx={{
//                     transition: "transform 0.3s, box-shadow 0.3s",
//                     "&:hover": {
//                       transform: "scale(1.05)",
//                       boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                     },
//                     // "&:hover": {
//                     //   textDecoration: "underline blue",
//                     //   color: "black",
//                     // },
//                   }}
//                 >
//                   <ListItemText
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                     }}
//                   >
//                     About Us
//                   </ListItemText>
//                 </ListItem>
//                 <ListItem
//                   component={Link}
//                   to="/admission"
//                   sx={{
//                     transition: "transform 0.3s, box-shadow 0.3s",
//                     "&:hover": {
//                       transform: "scale(1.05)",
//                       boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                     },
//                     // "&:hover": {
//                     //   textDecoration: "underline blue",
//                     //   color: "black",
//                     // },
//                   }}
//                 >
//                   <ListItemText
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                     }}
//                   >
//                     Admission
//                   </ListItemText>
//                 </ListItem>
//                 <ListItem
//                   component={Link}
//                   to="/event"
//                   sx={{
//                     transition: "transform 0.3s, box-shadow 0.3s",
//                     "&:hover": {
//                       transform: "scale(1.05)",
//                       boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                     },
//                     // "&:hover": {
//                     //   textDecoration: "underline blue",
//                     //   color: "black",
//                     // },
//                   }}
//                 >
//                   <ListItemText
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                     }}
//                   >
//                     Event
//                   </ListItemText>
//                 </ListItem>
//                 <ListItem
//                   component={Link}
//                   to="/circular"
//                   sx={{
//                     transition: "transform 0.3s, box-shadow 0.3s",
//                     "&:hover": {
//                       transform: "scale(1.05)",
//                       boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                     },
//                     // "&:hover": {
//                     //   textDecoration: "underline blue",
//                     //   color: "black",
//                     // },
//                   }}
//                 >
//                   <ListItemText
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                     }}
//                   >
//                     Circular
//                   </ListItemText>
//                 </ListItem>
//                 <ListItem
//                   component={Link}
//                   to="/contact"
//                   sx={{
//                     transition: "transform 0.3s, box-shadow 0.3s",
//                     "&:hover": {
//                       transform: "scale(1.05)",
//                       boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                     },
//                     // "&:hover": {
//                     //   textDecoration: "underline blue",
//                     //   color: "black",
//                     // },
//                   }}
//                 >
//                   <ListItemText
//                     primaryTypographyProps={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                     }}
//                   >
//                     Contact us
//                   </ListItemText>
//                 </ListItem>
//               </List>
//             </Container>
//           </Box>
//           <IconButton
//             edge="start"
//             color="inherit"
//             aria-label="menu"
//             onClick={toggleDrawer}
//             sx={{ display: { md: "none", color: "black" } }}
//           >
//             <Menu />
//           </IconButton>
//         </Toolbar>
//       </AppBar>

//       <Drawer
//         anchor="right"
//         open={isDrawerOpen}
//         onClose={toggleDrawer}
//         sx={{
//           "& .MuiDrawer-paper": {
//             width: "50%",
//             maxWidth: "200px",
//             backgroundColor: "#E2D1F9",
//             height: 400,
//           },
//         }}
//       >
//         <IconButton
//           color="inherit"
//           aria-label="close drawer"
//           edge="start"
//           onClick={toggleDrawer}
//           sx={{
//             alignSelf: "flex-start",
//             paddingLeft: "16px",
//             marginLeft: 5,
//             marginTop: 5,
//             color: "black",
//           }}
//         >
//           <Close />
//         </IconButton>
//         <Box
//           sx={{
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "center",
//             alignItems: "center",
//             height: "100%",
//           }}
//         >
//           <List>
//             {" "}
//             <ListItem
//               component={Link}
//               to="/"
//               sx={{
//                 fontSize: "18px",
//                 "&:hover": {
//                   textDecoration: "underline blue",
//                   color: "black",
//                 },
//               }}
//             >
//               <ListItemText
//                 primaryTypographyProps={{ fontSize: 16, fontWeight: "bold" }}
//               >
//                 Home
//               </ListItemText>
//             </ListItem>
//             <ListItem
//               component={Link}
//               to="/about"
//               sx={{
//                 fontSize: "18px",
//                 "&:hover": {
//                   textDecoration: "underline",
//                   color: "black",
//                 },
//               }}
//             >
//               <ListItemText
//                 primaryTypographyProps={{ fontSize: 16, fontWeight: "bold" }}
//               >
//                 About Us
//               </ListItemText>
//             </ListItem>
//             <ListItem
//               component={Link}
//               to="/admission"
//               sx={{
//                 "&:hover": {
//                   textDecoration: "underline blue",
//                   color: "black",
//                 },
//               }}
//             >
//               <ListItemText
//                 primaryTypographyProps={{ fontSize: 16, fontWeight: "bold" }}
//               >
//                 Admission
//               </ListItemText>
//             </ListItem>
//             <ListItem
//               component={Link}
//               to="/event"
//               sx={{
//                 "&:hover": {
//                   textDecoration: "underline blue",
//                   color: "black",
//                 },
//               }}
//             >
//               <ListItemText
//                 primaryTypographyProps={{ fontSize: 16, fontWeight: "bold" }}
//               >
//                 Event
//               </ListItemText>
//             </ListItem>
//             <ListItem
//               component={Link}
//               to="/circular"
//               sx={{
//                 "&:hover": {
//                   textDecoration: "underline blue",
//                   color: "black",
//                 },
//               }}
//             >
//               <ListItemText
//                 primaryTypographyProps={{ fontSize: 16, fontWeight: "bold" }}
//               >
//                 Circular
//               </ListItemText>
//             </ListItem>
//             <ListItem
//               component={Link}
//               to="/contact"
//               sx={{
//                 "&:hover": {
//                   textDecoration: "underline blue",
//                   color: "black",
//                 },
//               }}
//             >
//               <ListItemText
//                 primaryTypographyProps={{ fontSize: 16, fontWeight: "bold" }}
//               >
//                 Contact us
//               </ListItemText>
//             </ListItem>
//           </List>
//         </Box>
//       </Drawer>
//     </>
//   );
// }
